import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trophy, Flame, Gem, Users, RefreshCw, Zap, Save, History, KeyRound, Lock, Unlock, Plus, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { DailySuperAdminPanel } from './DailySuperAdminPanel';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const JackpotsAdminTab = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [history, setHistory] = useState([]);
    const [firing, setFiring] = useState('');
    const [edited, setEdited] = useState(null);
    const [pinSet, setPinSet] = useState(false);
    // Fire confirmation modal state
    const [fireKind, setFireKind] = useState('');
    const [firePin, setFirePin] = useState('');
    const [fireError, setFireError] = useState('');
    // Set/change PIN modal state
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [newPin, setNewPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [pinSaving, setPinSaving] = useState(false);
    // Top-up modal state
    const [topupKind, setTopupKind] = useState('');
    const [topupAmount, setTopupAmount] = useState('');
    const [topupNote, setTopupNote] = useState('');
    const [topupSaving, setTopupSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: cfg }, { data: hist }, { data: pinStatus }] = await Promise.all([
                axios.get(`${API}/admin/jackpot/config`, { withCredentials: true }),
                axios.get(`${API}/admin/jackpot/history?limit=20`, { withCredentials: true }),
                axios.get(`${API}/admin/jackpot/pin-status`, { withCredentials: true }),
            ]);
            setData(cfg);
            setEdited(cfg.config);
            setHistory(hist);
            setPinSet(pinStatus.pin_set);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load jackpot data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const saveConfig = async () => {
        try {
            await axios.put(`${API}/admin/jackpot/config`, edited, { withCredentials: true });
            toast.success('Jackpot config saved');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        }
    };

    // Open fire confirmation modal instead of firing directly
    const openFireDialog = (kind) => {
        setFireKind(kind);
        setFirePin('');
        setFireError('');
    };

    const closeFireDialog = () => {
        setFireKind('');
        setFirePin('');
        setFireError('');
    };

    const confirmFire = async () => {
        if (pinSet && !firePin.trim()) {
            setFireError('PIN required');
            return;
        }
        setFiring(fireKind);
        setFireError('');
        try {
            const { data: result } = await axios.post(
                `${API}/admin/jackpot/${fireKind}/fire`,
                pinSet ? { pin: firePin.trim() } : {},
                { withCredentials: true }
            );
            const label = fireKind === 'grand' ? 'Grand' : fireKind === 'super' ? 'Super' : 'BEToBuck$';
            toast.success(
                fireKind === 'super'
                    ? `${label} fired! ${result.winners[0]?.name} won $${result.distributed.toFixed(2)}`
                    : `${label} fired! $${result.distributed.toFixed(2)} split across ${result.winner_count} player(s)`
            );
            closeFireDialog();
            load();
        } catch (e) {
            setFireError(e.response?.data?.detail || 'Fire failed');
        } finally {
            setFiring('');
        }
    };

    const openPinDialog = () => {
        setNewPin('');
        setConfirmPin('');
        setPinDialogOpen(true);
    };

    const savePin = async () => {
        if (!/^\d{4,12}$/.test(newPin)) {
            toast.error('PIN must be 4-12 digits');
            return;
        }
        if (newPin !== confirmPin) {
            toast.error('PINs do not match');
            return;
        }
        setPinSaving(true);
        try {
            await axios.put(`${API}/admin/jackpot/pin`, { pin: newPin }, { withCredentials: true });
            toast.success('Jackpot PIN saved');
            setPinDialogOpen(false);
            setNewPin('');
            setConfirmPin('');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save PIN');
        } finally {
            setPinSaving(false);
        }
    };

    const clearPin = async () => {
        if (!confirm('Remove Jackpot PIN? Fire buttons will no longer require a PIN.')) return;
        try {
            await axios.delete(`${API}/admin/jackpot/pin`, { withCredentials: true });
            toast.success('Jackpot PIN removed');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to clear PIN');
        }
    };

    const primeSuper = async () => {
        try {
            await axios.post(`${API}/admin/jackpot/super/prime`, {}, { withCredentials: true });
            toast.success('Super jackpot primed for today');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Prime failed');
        }
    };

    const openTopupDialog = (kind) => {
        setTopupKind(kind);
        setTopupAmount('');
        setTopupNote('');
    };

    const closeTopupDialog = () => {
        setTopupKind('');
        setTopupAmount('');
        setTopupNote('');
    };

    const submitTopup = async () => {
        const amt = parseFloat(topupAmount);
        if (!amt || amt <= 0) {
            toast.error('Enter an amount > 0');
            return;
        }
        if (amt > 100000) {
            toast.error('Max top-up is $100,000');
            return;
        }
        setTopupSaving(true);
        try {
            await axios.post(`${API}/admin/jackpot/topup`,
                { kind: topupKind, amount: amt, note: topupNote.trim() || undefined },
                { withCredentials: true });
            const labelMap = { grand: 'Community Grand', super: 'Daily Super', blake: 'BEToBuck$' };
            toast.success(`+$${amt.toFixed(2)} added to ${labelMap[topupKind]}`);
            closeTopupDialog();
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Top-up failed');
        } finally {
            setTopupSaving(false);
        }
    };

    const toggleDay = (idx) => {
        const days = new Set(edited.super_enabled_days || []);
        if (days.has(idx)) days.delete(idx); else days.add(idx);
        setEdited({ ...edited, super_enabled_days: Array.from(days).sort() });
    };

    if (!data || !edited) {
        return <div className="text-[#A0AAB2] py-12 text-center">Loading jackpot console…</div>;
    }

    const pots = data.pots || {};
    const eligibleCount = data.eligible_count || 0;

    return (
        <div className="space-y-4">
            <DailySuperAdminPanel />
            {/* MEGA row — BEToBuck$ (full-width, top billing) */}
            <div className="card-dark p-5 relative overflow-hidden" style={{ borderColor: '#FF007F60', background: 'linear-gradient(135deg, rgba(255,0,127,0.08), rgba(255,215,0,0.03), rgba(0,0,0,0.3))' }} data-testid="admin-blake-pot">
                <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#FF007F]/10 blur-3xl pointer-events-none" />
                <div className="relative flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <Gem className="w-5 h-5 text-[#FF007F]" />
                        <div>
                            <div className="flex items-center gap-2">
                                <span className="text-[#FF007F] font-black text-sm uppercase tracking-widest">BEToBuck$</span>
                                <span className="text-[9px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FF007F]/15 text-[#FF007F] border border-[#FF007F]/40">
                                    Mega · Rare
                                </span>
                            </div>
                            <p className="text-[10px] text-[#A0AAB2] mt-0.5">Resets to ${edited.blake_seed?.toFixed(2)} after fire · community split across eligible players</p>
                        </div>
                    </div>
                    <p className="text-3xl sm:text-4xl font-mono font-black text-[#FF007F]">${pots.blake?.toFixed(2)}</p>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openTopupDialog('blake')}
                            className="border-[#FF007F]/40 text-[#FF007F] hover:bg-[#FF007F]/10 h-8 text-xs"
                            data-testid="topup-blake-btn"
                        >
                            <Plus className="w-3 h-3 mr-1" />Top up
                        </Button>
                        <Button
                            size="sm"
                            onClick={() => openFireDialog('blake')}
                            disabled={firing === 'blake'}
                            className="bg-[#FF007F] text-white hover:bg-[#FF007F]/90 h-8 text-xs"
                            data-testid="fire-blake-btn"
                        >
                            <Zap className="w-3 h-3 mr-1" />
                            {firing === 'blake' ? 'Firing…' : 'Fire'}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Live Pots Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="card-dark p-5" style={{ borderColor: '#FFD70060' }} data-testid="admin-grand-pot">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Trophy className="w-4 h-4 text-[#FFD700]" />
                            <span className="text-[#FFD700] text-xs font-bold uppercase">Community Grand</span>
                        </div>
                        <div className="flex gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openTopupDialog('grand')}
                                className="border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 h-7 text-xs px-2"
                                data-testid="topup-grand-btn"
                            >
                                <Plus className="w-3 h-3" />
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => openFireDialog('grand')}
                                disabled={firing === 'grand'}
                                className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 h-7 text-xs"
                                data-testid="fire-grand-btn"
                            >
                                <Zap className="w-3 h-3 mr-1" />
                                {firing === 'grand' ? 'Firing…' : 'Fire'}
                            </Button>
                        </div>
                    </div>
                    <p className="text-3xl font-mono font-black text-[#FFD700]">${pots.grand?.toFixed(2)}</p>
                    <p className="text-[10px] text-[#A0AAB2] mt-1">Resets to ${edited.grand_seed?.toFixed(2)} after fire · splits across all eligible players</p>
                </div>

                <div className="card-dark p-5"
                     style={{ borderColor: pots.super_active ? '#FF3B3060' : '#A0AAB220' }}
                     data-testid="admin-super-pot">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Flame className={`w-4 h-4 ${pots.super_active ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`} />
                            <span className={`text-xs font-bold uppercase ${pots.super_active ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`}>
                                Super Pot {pots.super_active ? '· LIVE' : '· OFF'}
                            </span>
                        </div>
                        <div className="flex gap-1">
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openTopupDialog('super')}
                                disabled={!pots.super_active}
                                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 h-7 text-xs px-2 disabled:opacity-40"
                                data-testid="topup-super-btn"
                            >
                                <Plus className="w-3 h-3" />
                            </Button>
                            <Button
                                size="sm"
                                onClick={() => openFireDialog('super')}
                                disabled={firing === 'super' || !pots.super_active}
                                className="bg-[#FF3B30] text-white hover:bg-[#FF3B30]/90 h-7 text-xs"
                                data-testid="fire-super-btn"
                            >
                                <Zap className="w-3 h-3 mr-1" />
                                {firing === 'super' ? 'Firing…' : 'Fire'}
                            </Button>
                        </div>
                    </div>
                    <p className={`text-3xl font-mono font-black ${pots.super_active ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`}>
                        ${pots.super?.toFixed(2)}
                    </p>
                    <p className="text-[10px] text-[#A0AAB2] mt-1">
                        {pots.super_active
                            ? `Active for ${pots.super_active_date}`
                            : 'Not primed today'}
                    </p>
                </div>

                <div className="card-dark p-5" data-testid="admin-eligible-card">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4 text-[#39FF14]" />
                            <span className="text-[#39FF14] text-xs font-bold uppercase">Eligible Now</span>
                        </div>
                        <Button size="sm" variant="ghost" onClick={load} className="text-[#A0AAB2] h-7 text-xs">
                            <RefreshCw className="w-3 h-3" />
                        </Button>
                    </div>
                    <p className="text-3xl font-mono font-black text-[#39FF14]">{eligibleCount}</p>
                    <p className="text-[10px] text-[#A0AAB2] mt-1">
                        {edited.eligible_min_spins}+ spins in last {edited.eligible_window_minutes} min
                    </p>
                </div>
            </div>

            {/* Eligible preview */}
            {data.eligible_preview?.length > 0 && (
                <div className="card-dark p-4">
                    <p className="text-xs uppercase text-[#A0AAB2] font-bold mb-2">Currently Eligible</p>
                    <div className="flex flex-wrap gap-2">
                        {data.eligible_preview.map(p => (
                            <span key={p.user_id}
                                  className="px-2 py-1 rounded-full text-[11px] bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/30">
                                {p.name} · {p.spin_count} spins · ${p.wager_sum.toFixed(2)}
                            </span>
                        ))}
                    </div>
                </div>
            )}

            {/* Auto-tick (synthetic hourly growth) */}
            <div className="card-dark p-5" style={{ borderColor: edited.auto_tick_enabled ? '#39FF1460' : '#A0AAB220' }} data-testid="auto-tick-card">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Clock className={`w-4 h-4 ${edited.auto_tick_enabled ? 'text-[#39FF14]' : 'text-[#A0AAB2]'}`} />
                        <h3 className="text-sm uppercase font-bold text-white tracking-wide">Hourly Auto-Tick</h3>
                        <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded-full ${edited.auto_tick_enabled ? 'bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/40' : 'bg-white/5 text-[#A0AAB2] border border-white/10'}`}>
                            {edited.auto_tick_enabled ? 'ON' : 'OFF'}
                        </span>
                    </div>
                    <Switch
                        checked={!!edited.auto_tick_enabled}
                        onCheckedChange={v => setEdited({ ...edited, auto_tick_enabled: v })}
                        data-testid="auto-tick-switch"
                    />
                </div>
                <p className="text-[11px] text-[#A0AAB2] mb-3">
                    Live drip — keeps pots visually alive. Ticks every minute at <span className="text-white font-bold">1/60th</span> of the per-hour rate (e.g. $6/hr ≈ $0.10 every minute). Save Config below to apply.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <Label className="text-white text-xs">Grand $/hour</Label>
                        <Input type="number" step="1" min="0"
                               value={edited.auto_tick_grand_per_hour ?? 0}
                               onChange={e => setEdited({ ...edited, auto_tick_grand_per_hour: parseFloat(e.target.value) || 0 })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="auto-tick-grand" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Super $/hour <span className="text-[#A0AAB2]">(only when active)</span></Label>
                        <Input type="number" step="1" min="0"
                               value={edited.auto_tick_super_per_hour ?? 0}
                               onChange={e => setEdited({ ...edited, auto_tick_super_per_hour: parseFloat(e.target.value) || 0 })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="auto-tick-super" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">BEToBuck$ $/hour</Label>
                        <Input type="number" step="1" min="0"
                               value={edited.auto_tick_blake_per_hour ?? 0}
                               onChange={e => setEdited({ ...edited, auto_tick_blake_per_hour: parseFloat(e.target.value) || 0 })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="auto-tick-blake" />
                    </div>
                </div>
                <div className="mt-3 flex items-end gap-3">
                    <div className="flex-1 max-w-xs">
                        <Label className="text-white text-xs">
                            Pause when idle <span className="text-[#A0AAB2]">(minutes)</span>
                        </Label>
                        <Input type="number" step="1" min="0"
                               value={edited.auto_tick_idle_pause_minutes ?? 3}
                               onChange={e => setEdited({ ...edited, auto_tick_idle_pause_minutes: parseInt(e.target.value) || 0 })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="auto-tick-idle-pause" />
                    </div>
                    <p className="text-[11px] text-[#A0AAB2] flex-1 pb-1">
                        Pots stop growing if no players have been online in the last <span className="text-white font-bold">{edited.auto_tick_idle_pause_minutes ?? 3}</span> minutes. Set to 0 to disable the pause.
                    </p>
                </div>
            </div>

            {/* Config */}
            <div className="card-dark p-5 space-y-4">
                <h3 className="text-sm uppercase font-bold text-white tracking-wide">Configuration</h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <Label className="text-white text-xs">Grand contribution % (per slot wager)</Label>
                        <Input type="number" step="0.1" value={edited.grand_contribution_pct}
                               onChange={e => setEdited({ ...edited, grand_contribution_pct: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-grand-pct" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Grand seed (reset amount)</Label>
                        <Input type="number" step="10" value={edited.grand_seed}
                               onChange={e => setEdited({ ...edited, grand_seed: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-grand-seed" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Promote on home when Grand &ge;</Label>
                        <Input type="number" step="100" value={edited.promote_grand_min}
                               onChange={e => setEdited({ ...edited, promote_grand_min: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-promote-min" />
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        <Label className="text-white text-xs">🔥 "Hot" visuals kick in when Grand &ge;</Label>
                        <Input type="number" step="100" value={edited.grand_hot_min}
                               onChange={e => setEdited({ ...edited, grand_hot_min: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-grand-hot-min" />
                        <p className="text-[9px] text-[#A0AAB2] mt-1">Above this, Grand gets fiery glow & "HOT" badge to build anticipation.</p>
                    </div>
                    <div>
                        <Label className="text-white text-xs">💎 "Almost Ready" visuals kick in when BETo Bucks &ge;</Label>
                        <Input type="number" step="500" value={edited.blake_hot_min}
                               onChange={e => setEdited({ ...edited, blake_hot_min: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-blake-hot-min" />
                        <p className="text-[9px] text-[#A0AAB2] mt-1">BETo Bucks gets intensified magenta glow & "ALMOST READY" badge above this.</p>
                    </div>
                </div>

                {/* BETohalics Bucks config */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-white/5">
                    <div className="sm:col-span-3 flex items-center gap-2">
                        <Gem className="w-3.5 h-3.5 text-[#FF007F]" />
                        <span className="text-[11px] uppercase tracking-wider font-bold text-[#FF007F]">BETohalics Bucks (Mega)</span>
                    </div>
                    <div>
                        <Label className="text-white text-xs">BETo Bucks contribution % (per slot wager)</Label>
                        <Input type="number" step="0.05" value={edited.blake_contribution_pct}
                               onChange={e => setEdited({ ...edited, blake_contribution_pct: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-blake-pct" />
                        <p className="text-[9px] text-[#A0AAB2] mt-1">Lower = slower grow. 0.25% is the default slow progressive.</p>
                    </div>
                    <div>
                        <Label className="text-white text-xs">BETo Bucks seed (reset amount)</Label>
                        <Input type="number" step="500" value={edited.blake_seed}
                               onChange={e => setEdited({ ...edited, blake_seed: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-blake-seed" />
                        <p className="text-[9px] text-[#A0AAB2] mt-1">Where the pot starts — makes it feel big from day one.</p>
                    </div>
                    <div>
                        <Label className="text-white text-xs">Promote on home when BETo Bucks &ge;</Label>
                        <Input type="number" step="500" value={edited.promote_blake_min}
                               onChange={e => setEdited({ ...edited, promote_blake_min: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-blake-promote" />
                    </div>
                </div>

                {/* Super master ON/OFF toggle */}
                <div
                    className="flex items-center justify-between p-4 rounded-lg border gap-4"
                    style={{
                        backgroundColor: edited.super_enabled ? 'rgba(255,59,48,0.08)' : 'rgba(160,170,178,0.05)',
                        borderColor: edited.super_enabled ? 'rgba(255,59,48,0.4)' : 'rgba(255,255,255,0.1)',
                    }}
                    data-testid="super-master-toggle-row"
                >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                        <Flame className={`w-5 h-5 shrink-0 ${edited.super_enabled ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`} />
                        <div className="min-w-0">
                            <p className={`text-sm font-bold ${edited.super_enabled ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`}>
                                Daily Super Jackpot — Master Switch
                            </p>
                            <p className="text-[11px] text-[#A0AAB2] mt-0.5">
                                {edited.super_enabled
                                    ? "Super is RUNNING. It will prime + grow on the days you've enabled below."
                                    : "Super is OFF. It won't prime, grow, or fire on any day until you turn it back on."}
                            </p>

                            {/* Schedule summary badge — only meaningful when master is ON */}
                            {edited.super_enabled && (() => {
                                const days = (edited.super_enabled_days || []).slice().sort((a, b) => a - b);
                                const labels = days.map(d => DAY_LABELS[d]);
                                let scheduleText, scheduleColor, scheduleBg;
                                if (days.length === 0) {
                                    scheduleText = '⚠ No days selected — Super won\'t fire';
                                    scheduleColor = '#FF3B30';
                                    scheduleBg = 'rgba(255,59,48,0.15)';
                                } else if (days.length === 7) {
                                    scheduleText = 'Schedule: Every day';
                                    scheduleColor = '#39FF14';
                                    scheduleBg = 'rgba(57,255,20,0.12)';
                                } else {
                                    scheduleText = `Schedule: ${labels.join(' · ')}`;
                                    scheduleColor = '#FFD700';
                                    scheduleBg = 'rgba(255,215,0,0.12)';
                                }
                                return (
                                    <span
                                        className="inline-block mt-2 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border"
                                        style={{
                                            color: scheduleColor,
                                            backgroundColor: scheduleBg,
                                            borderColor: scheduleColor + '60',
                                        }}
                                        data-testid="super-schedule-badge"
                                    >
                                        {scheduleText}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>
                    <Switch
                        checked={!!edited.super_enabled}
                        onCheckedChange={(v) => setEdited({ ...edited, super_enabled: v })}
                        data-testid="super-master-toggle"
                    />
                </div>

                <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${edited.super_enabled ? '' : 'opacity-40 pointer-events-none'}`}>
                    <div>
                        <Label className="text-white text-xs">Super daily seed</Label>
                        <Input type="number" step="10" value={edited.super_seed}
                               onChange={e => setEdited({ ...edited, super_seed: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-super-seed" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Super growth % (per slot wager)</Label>
                        <Input type="number" step="0.1" value={edited.super_growth_pct}
                               onChange={e => setEdited({ ...edited, super_growth_pct: parseFloat(e.target.value) })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-super-pct" />
                    </div>
                    <div className="flex items-end gap-2">
                        <Button onClick={primeSuper} variant="outline"
                                className="border-[#FF3B30]/40 text-[#FF3B30] hover:bg-[#FF3B30]/10 w-full"
                                data-testid="prime-super-btn">
                            <Flame className="w-4 h-4 mr-1" />
                            Prime Super (today)
                        </Button>
                    </div>
                </div>

                <div className={edited.super_enabled ? '' : 'opacity-40 pointer-events-none'}>
                    <Label className="text-white text-xs">Super Jackpot enabled days (CT)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                        {DAY_LABELS.map((d, idx) => (
                            <button
                                key={idx}
                                onClick={() => toggleDay(idx)}
                                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
                                    edited.super_enabled_days?.includes(idx)
                                        ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                                        : 'bg-[#0a0a0a] text-[#A0AAB2] border-white/10 hover:border-[#FF3B30]/40'
                                }`}
                                data-testid={`cfg-day-${d.toLowerCase()}`}
                            >
                                {d}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                        <Label className="text-white text-xs">Eligibility window (minutes)</Label>
                        <Input type="number" step="1" value={edited.eligible_window_minutes}
                               onChange={e => setEdited({ ...edited, eligible_window_minutes: parseInt(e.target.value) || 1 })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-window" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Min spins in window</Label>
                        <Input type="number" step="1" value={edited.eligible_min_spins}
                               onChange={e => setEdited({ ...edited, eligible_min_spins: parseInt(e.target.value) || 1 })}
                               className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                               data-testid="cfg-min-spins" />
                    </div>
                </div>

                <Button onClick={saveConfig} disabled={loading}
                        className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 font-bold"
                        data-testid="save-jackpot-config">
                    <Save className="w-4 h-4 mr-1" />
                    Save Configuration
                </Button>
            </div>

            {/* Fire history */}
            <div className="card-dark p-5">
                <div className="flex items-center gap-2 mb-3">
                    <History className="w-4 h-4 text-[#FFD700]" />
                    <h3 className="text-sm uppercase font-bold text-white tracking-wide">Fire History</h3>
                </div>
                {history.length === 0 ? (
                    <p className="text-xs text-[#A0AAB2]">No jackpots fired yet.</p>
                ) : (
                    <div className="space-y-2">
                        {history.map((h, i) => (
                            <div key={i}
                                 className="flex items-center justify-between p-3 rounded-lg bg-[#0a0a0a] border border-white/5 text-xs"
                                 data-testid={`jackpot-history-${i}`}>
                                <div className="flex items-center gap-2">
                                    {h.type === 'grand' ? <Trophy className="w-4 h-4 text-[#FFD700]" />
                                        : h.type === 'blake' ? <Gem className="w-4 h-4 text-[#FF007F]" />
                                        : <Flame className="w-4 h-4 text-[#FF3B30]" />}
                                    <span className={`font-bold uppercase ${h.type === 'grand' ? 'text-[#FFD700]' : h.type === 'blake' ? 'text-[#FF007F]' : 'text-[#FF3B30]'}`}>
                                        {h.type}
                                    </span>
                                    <span className="text-[#A0AAB2]">
                                        {new Date(h.fired_at).toLocaleString('en-US', { timeZone: 'America/Chicago' })} CT
                                    </span>
                                </div>
                                <div className="text-right">
                                    <p className="text-white font-mono font-bold">${h.distributed?.toFixed(2)}</p>
                                    <p className="text-[#A0AAB2]">{h.winner_count} winner{h.winner_count !== 1 ? 's' : ''}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Jackpot PIN Settings */}
            <div className="card-dark p-5 space-y-3" data-testid="jackpot-pin-card">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <KeyRound className={`w-4 h-4 ${pinSet ? 'text-[#39FF14]' : 'text-[#A0AAB2]'}`} />
                        <h3 className="text-sm uppercase font-bold text-white tracking-wide">Jackpot Fire PIN</h3>
                        {pinSet ? (
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/40">
                                <Lock className="w-2.5 h-2.5 inline mr-0.5" />
                                Protected
                            </span>
                        ) : (
                            <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#FF3B30]/15 text-[#FF3B30] border border-[#FF3B30]/40">
                                <Unlock className="w-2.5 h-2.5 inline mr-0.5" />
                                Not Set
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <Button size="sm" onClick={openPinDialog}
                                className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 h-8 text-xs"
                                data-testid="set-pin-btn">
                            {pinSet ? 'Change PIN' : 'Set PIN'}
                        </Button>
                        {pinSet && (
                            <Button size="sm" variant="ghost" onClick={clearPin}
                                    className="text-[#FF3B30] hover:bg-[#FF3B30]/10 h-8 text-xs"
                                    data-testid="clear-pin-btn">
                                Remove
                            </Button>
                        )}
                    </div>
                </div>
                <p className="text-[11px] text-[#A0AAB2]">
                    {pinSet
                        ? 'All three Fire buttons require this PIN. Prevents accidental fires. Change or remove anytime.'
                        : 'No PIN set — fire buttons will fire immediately after confirmation. Set a 4–12 digit PIN to add a safety lock.'}
                </p>
            </div>

            {/* Fire confirmation dialog (PIN-gated when PIN is set) */}
            <Dialog open={!!fireKind} onOpenChange={(o) => !o && closeFireDialog()}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            {fireKind === 'blake' ? <Gem className="w-5 h-5 text-[#FF007F]" />
                                : fireKind === 'grand' ? <Trophy className="w-5 h-5 text-[#FFD700]" />
                                : <Flame className="w-5 h-5 text-[#FF3B30]" />}
                            Fire {fireKind === 'blake' ? 'BEToBuck$' : fireKind === 'grand' ? 'Community Grand' : 'Daily Super'}?
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            This will pay out the full pot to eligible players and reset it to the seed amount. This cannot be undone.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="space-y-3 py-2">
                        {/* Show pot being fired */}
                        <div className="p-3 rounded-lg bg-[#0a0a0a] border border-white/5 text-center">
                            <p className="text-xs text-[#A0AAB2] uppercase tracking-wider mb-1">Pot to distribute</p>
                            <p className={`text-3xl font-mono font-black ${
                                fireKind === 'blake' ? 'text-[#FF007F]' : fireKind === 'grand' ? 'text-[#FFD700]' : 'text-[#FF3B30]'
                            }`}>
                                ${(fireKind === 'blake' ? pots.blake : fireKind === 'grand' ? pots.grand : pots.super)?.toFixed(2)}
                            </p>
                            <p className="text-[11px] text-[#A0AAB2] mt-1">
                                {eligibleCount} eligible player{eligibleCount !== 1 ? 's' : ''} right now
                            </p>
                        </div>

                        {pinSet && (
                            <div>
                                <Label className="text-white text-xs flex items-center gap-1">
                                    <KeyRound className="w-3 h-3" />
                                    Enter Jackpot PIN to confirm
                                </Label>
                                <Input
                                    type="password"
                                    inputMode="numeric"
                                    maxLength={12}
                                    value={firePin}
                                    onChange={e => setFirePin(e.target.value.replace(/\D/g, ''))}
                                    onKeyDown={(e) => e.key === 'Enter' && confirmFire()}
                                    placeholder="••••"
                                    className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono text-center text-lg tracking-[0.5em]"
                                    autoFocus
                                    data-testid="fire-pin-input"
                                />
                            </div>
                        )}

                        {fireError && (
                            <p className="text-[#FF3B30] text-sm text-center" data-testid="fire-error">{fireError}</p>
                        )}
                    </div>

                    <DialogFooter className="flex-row gap-2">
                        <Button variant="ghost" onClick={closeFireDialog} className="flex-1 text-[#A0AAB2]">
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmFire}
                            disabled={!!firing || (pinSet && !firePin.trim())}
                            className={`flex-1 font-bold ${
                                fireKind === 'blake' ? 'bg-[#FF007F] hover:bg-[#FF007F]/90 text-white'
                                    : fireKind === 'grand' ? 'bg-[#FFD700] hover:bg-[#FFD700]/90 text-black'
                                    : 'bg-[#FF3B30] hover:bg-[#FF3B30]/90 text-white'
                            }`}
                            data-testid="confirm-fire-btn"
                        >
                            <Zap className="w-4 h-4 mr-1" />
                            {firing ? 'Firing…' : 'Confirm Fire'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Set / Change PIN dialog */}
            <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-[#FFD700]" />
                            {pinSet ? 'Change' : 'Set'} Jackpot PIN
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            4–12 digits. Required to fire any jackpot. Remember it — there's no recovery, only a "Remove" option.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-white text-xs">New PIN</Label>
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={12}
                                value={newPin}
                                onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))}
                                placeholder="••••"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono text-center text-lg tracking-[0.5em]"
                                autoFocus
                                data-testid="new-pin-input"
                            />
                        </div>
                        <div>
                            <Label className="text-white text-xs">Confirm PIN</Label>
                            <Input
                                type="password"
                                inputMode="numeric"
                                maxLength={12}
                                value={confirmPin}
                                onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))}
                                onKeyDown={(e) => e.key === 'Enter' && savePin()}
                                placeholder="••••"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white font-mono text-center text-lg tracking-[0.5em]"
                                data-testid="confirm-pin-input"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={savePin}
                            disabled={pinSaving || newPin.length < 4 || newPin !== confirmPin}
                            className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90 font-bold w-full"
                            data-testid="save-pin-btn"
                        >
                            {pinSaving ? 'Saving…' : 'Save PIN'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Top-up Pot Dialog */}
            <Dialog open={!!topupKind} onOpenChange={(open) => !open && closeTopupDialog()}>
                <DialogContent className="bg-[#121212] border-white/10 max-w-sm">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <Plus className="w-4 h-4 text-[#FFD700]" />
                            Top up {topupKind === 'blake' ? 'BEToBuck$' : topupKind === 'grand' ? 'Community Grand' : topupKind === 'super' ? 'Daily Super' : ''}
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Add credits directly to this pot. Used for promo events, pot smoothing, or marketing boosts. Audit-logged.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label className="text-white text-xs">Amount ($)</Label>
                            <Input
                                type="number" step="1" min="0.01" max="100000"
                                value={topupAmount}
                                onChange={e => setTopupAmount(e.target.value)}
                                placeholder="e.g. 250"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white text-lg font-mono"
                                autoFocus
                                data-testid="topup-amount-input"
                            />
                            <div className="flex gap-1.5 mt-2 flex-wrap">
                                {[25, 100, 500, 1000].map(v => (
                                    <button
                                        key={v}
                                        type="button"
                                        onClick={() => setTopupAmount(String(v))}
                                        className="text-[11px] px-2 py-1 rounded-md bg-white/5 text-white hover:bg-white/10 border border-white/10"
                                        data-testid={`topup-quick-${v}`}
                                    >
                                        ${v}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label className="text-white text-xs">Note <span className="text-[#A0AAB2] normal-case">(optional)</span></Label>
                            <Input
                                value={topupNote}
                                onChange={e => setTopupNote(e.target.value)}
                                placeholder="e.g. Friday night promo boost"
                                maxLength={200}
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="topup-note-input"
                            />
                        </div>
                    </div>
                    <DialogFooter className="gap-2">
                        <Button
                            variant="outline"
                            onClick={closeTopupDialog}
                            className="border-white/20 text-white"
                            disabled={topupSaving}
                            data-testid="topup-cancel-btn"
                        >Cancel</Button>
                        <Button
                            onClick={submitTopup}
                            disabled={topupSaving || !topupAmount}
                            className="bg-[#FFD700] text-black hover:bg-[#FFD700]/90"
                            data-testid="topup-confirm-btn"
                        >
                            {topupSaving ? 'Adding…' : `+ Add to pot`}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
